import { useQuery } from '@tanstack/react-query';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import {
  DASHBOARD_CONSTANTS,
  VEHICLE_VOLUME_BUCKETS,
} from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type { SeriesItem } from '@/shared/components/ui/LineChart/LineChart';
import type {
  TrafficVolumeHourlyParams,
  TrafficVolumeHourlySeries,
} from '../types/dashboard.types';

// Aggregate the 8 per-class hourly arrays into the 4 display buckets, summing
// member classes element-wise per hour, and attach each bucket's colorStore key.
const toSeries = (raw: TrafficVolumeHourlySeries[] | undefined, hours: number): SeriesItem[] => {
  const byCode = new Map((raw ?? []).map((s) => [s.object_class_code, s.data]));
  return VEHICLE_VOLUME_BUCKETS.map((bucket) => ({
    name: bucket.title,
    color: bucket.color,
    data: Array.from({ length: hours }, (_, i) =>
      bucket.classes.reduce((sum, code) => sum + (byCode.get(code)?.[i] ?? 0), 0)
    ),
  }));
};

// PSC_PC0203001 - per-hour vehicle volume for the day (peak-hours line chart).
export const useTrafficVolumeHourly = (params: TrafficVolumeHourlyParams = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficVolumeHourlyByVehicle', params],
    queryFn: () => dashboardApi.getTrafficVolumeHourlyByVehicle(params),
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  const hours = data?.data?.hours ?? [];
  return {
    xAxisData: hours,
    series: toSeries(data?.data?.series, hours.length),
    isLoading,
    isError,
  };
};
